import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import { useServicePlanApi } from '../services/servicePlanApi';

function ViewQuestionnaireModal({ open, onClose, questionnaire }) {
  const { questionnairesApi } = useServicePlanApi();
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && questionnaire) {
      fetchFullData();
    }
  }, [open, questionnaire]);

  const fetchFullData = async () => {
    setLoading(true);
    try {
      const response = await questionnairesApi.getById(questionnaire.id);
      setFullData(response.data.data || response.data);
      console.log('Full questionnaire data:', response.data);
    } catch (error) {
      console.error('Failed to fetch questionnaire details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!questionnaire) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <span>{questionnaire.name}</span>
          <MDBadge
            badgeContent={questionnaire.isActive ? 'Active' : 'Inactive'}
            color={questionnaire.isActive ? 'success' : 'secondary'}
            variant="gradient"
            size="sm"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <MDTypography variant="body2" ml={2}>Loading details...</MDTypography>
          </Box>
        ) : (
          <MDBox pt={2}>
            {/* Page Info */}
            <MDTypography variant="h6" mb={2}>
              Linked Page
            </MDTypography>
            <Box p={2} bgcolor="grey.100" borderRadius={1} mb={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text.secondary" display="block">
                    Page Name
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {fullData?.page?.name || questionnaire.page?.name || 'N/A'}
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text.secondary" display="block">
                    Route
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {fullData?.page?.route || questionnaire.page?.route || 'N/A'}
                  </MDTypography>
                </Grid>
                <Grid item xs={6}>
                  <MDTypography variant="caption" color="text.secondary" display="block">
                    Page Status
                  </MDTypography>
                  <Chip 
                    label={fullData?.page?.active ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={fullData?.page?.active ? 'success' : 'default'}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Questionnaire Details */}
            <MDTypography variant="h6" mb={2}>
              Questionnaire Settings
            </MDTypography>

            {questionnaire.description && (
              <Box mb={2}>
                <MDTypography variant="caption" color="text.secondary" display="block">
                  Description
                </MDTypography>
                <MDTypography variant="body2">
                  {questionnaire.description}
                </MDTypography>
              </Box>
            )}

            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  CTA Button Text
                </MDTypography>
                <Chip 
                  label={questionnaire.ctaButtonText} 
                  size="small" 
                  color="primary"
                />
              </Grid>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  CTA Button Position
                </MDTypography>
                <Chip 
                  label={questionnaire.ctaButtonPosition.toUpperCase()} 
                  size="small" 
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Box mb={2}>
              <MDTypography variant="caption" color="text.secondary" display="block">
                Completion Message
              </MDTypography>
              <MDTypography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{questionnaire.completionMessage}"
              </MDTypography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Questions Summary */}
            <MDTypography variant="h6" mb={2}>
              Questions ({fullData?.questions?.length || 0})
            </MDTypography>
            {fullData?.questions && fullData.questions.length > 0 ? (
              <Box>
                {fullData.questions.map((q, index) => (
                  <Box 
                    key={q.id} 
                    p={2} 
                    mb={1} 
                    border="1px solid" 
                    borderColor="grey.300" 
                    borderRadius={1}
                    bgcolor={q.isActive ? 'transparent' : 'grey.50'}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <MDTypography variant="button">
                          {index + 1}. {q.text}
                        </MDTypography>
                        {q.helperText && (
                          <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            Help: {q.helperText}
                          </MDTypography>
                        )}
                      </Box>
                      <Box display="flex" gap={0.5} ml={2}>
                        <Chip label={q.type} size="small" color="info" />
                        {!q.isActive && <Chip label="Inactive" size="small" color="default" />}
                        {q.isRequired && <Chip label="Required" size="small" color="error" variant="outlined" />}
                      </Box>
                    </Box>
                    <MDTypography variant="caption" color="text.secondary" display="block" mt={1}>
                      {q.options?.length || 0} answer options • 
                      Order: {q.orderIndex} • 
                      {q.affectsPlanSelection ? ' Affects plan selection' : ' Informational only'}
                    </MDTypography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box 
                p={3} 
                textAlign="center" 
                border="2px dashed" 
                borderColor="grey.300" 
                borderRadius={1}
                bgcolor="grey.50"
              >
                <MDTypography variant="body2" color="text.secondary">
                  No questions added yet. 
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" display="block" mt={1}>
                  Go to the <strong>Questions</strong> tab to add questions to this questionnaire.
                </MDTypography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Timestamps */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text.secondary" display="block">
                  Created At
                </MDTypography>
                <MDTypography variant="caption">
                  {new Date(questionnaire.createdAt).toLocaleString()}
                </MDTypography>
              </Grid>
              <Grid item xs={6}>
                <MDTypography variant="caption" color="text.secondary" display="block">
                  Updated At
                </MDTypography>
                <MDTypography variant="caption">
                  {new Date(questionnaire.updatedAt).toLocaleString()}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="info">
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default ViewQuestionnaireModal;
